import { StackBehaviourSubject } from '@stack/react-shared'

export class StoredSubject<T> extends StackBehaviourSubject<T> {
	constructor(
		key: string,
		defaultValue: T,
		getFromStorage: (key: string) => T | undefined | null,
		setToStorage: (key: string, value: T) => void
	) {
		super(defaultValue)
		if (localStorage.getItem(key) === null) {
			setToStorage(key, defaultValue)
		}
		let savedValue = getFromStorage(key) as T
		if (savedValue === undefined) {
			savedValue = defaultValue
		}
		this.next(savedValue)
		this.subscribe(x => setToStorage(key, x))
	}
}

export class StoredStringSubject<T extends string | undefined = string> extends StoredSubject<T> {
	constructor(key: string, defaultValue: string | undefined) {
		super(
			key,
			defaultValue as T,
			key => localStorage.getItem(key) as T | undefined,
			(key, value) => localStorage.setItem(key, value ?? '')
		)
	}
}

export class StoredBooleanSubject extends StoredSubject<boolean> {
	constructor(key: string, defaultValue: boolean) {
		super(
			key,
			defaultValue,
			key => localStorage.getItem(key) === 'true',
			(key, value) => localStorage.setItem(key, value.toString())
		)
	}
}

export class StoredNumberSubject extends StoredSubject<number> {
	constructor(key: string, defaultValue: number) {
		super(
			key,
			defaultValue,
			key => Number(localStorage.getItem(key)),
			(key, value) => localStorage.setItem(key, value.toString())
		)
	}
}

export class StoredObjectSubject<T> extends StoredSubject<T> {
	constructor(key: string, defaultValue: T) {
		super(
			key,
			defaultValue,
			key => {
				const item = localStorage.getItem(key)
				try {
					return JSON.parse(item ?? 'null') as T
				} catch {
					return defaultValue
				}
			},
			(key, value) => localStorage.setItem(key, JSON.stringify(value))
		)
	}
}
